import { Injectable } from "@angular/core";
import { Observable, of } from "rxjs";
import { catchError, switchMap, map } from "rxjs/operators";
import { DocMetadataHandler } from "./add-doc-metadata.interface";
import { DocMetadataDto, DocMetadataGroup, DocMetadataInfo, EntityType } from "../model/doc-metadata";

/**
 * Metadata handler used when creating a NEW VERSION of a rev document.
 *
 * Document metadata is forced to be identical across a version tree, so on a new version the
 * fields are pre-filled from the parent version's stored metadata and rendered read-only.
 * The backend (`create_rev_doc`) is authoritative and also copies the parent's metadata; this
 * handler only keeps the UI consistent with that behaviour.
 */
@Injectable()
export class VersionDocMetadataHandler extends DocMetadataHandler {

    /** doc_id of any existing version in the tree (used to read the shared metadata values). */
    parentDocId: number | null = null;

    override getDocMetadataDefinition(envId: number, catVersionId: number, lang: string,
                                      assetType: string | null, entityType: EntityType): Observable<DocMetadataInfo> {
        return super.getDocMetadataDefinition(envId, catVersionId, lang, assetType, entityType).pipe(
            switchMap((info: DocMetadataInfo) => {
                if (this.parentDocId == null) {
                    return of(info);
                }
                return this.getDocMetadata(this.parentDocId, lang, true).pipe(
                    map((parentMetadata: DocMetadataDto[]) => {
                        this.applyParentSelections(parentMetadata);
                        return info;
                    }),
                    catchError(() => of(info))
                );
            })
        );
    }

    override isFormControlDisabled(group: DocMetadataGroup) {
        // Everything is read-only on a new version; otherwise fall back to the base rule
        // (default-valued fields stay locked).
        return this.parentDocId != null || super.isFormControlDisabled(group);
    }

    private applyParentSelections(parentMetadata: DocMetadataDto[]) {
        if (parentMetadata == null || parentMetadata.length == 0) {
            return;
        }

        for (const field of parentMetadata) {
            const groupMetadata = this.docMetadataByGroup[field.field_name];
            const group = this.groups.find((g) => g.groupName == field.field_name);
            if (!groupMetadata || !group) {
                continue;
            }

            const selectedCodes = (field.lov ?? []).map((v) => v.value_code);
            const preselect = groupMetadata.filter((m) => selectedCodes.indexOf(m.fieldValue) != -1);
            if (preselect.length == 0) {
                continue;
            }

            this.selections[field.field_name] = group.isMultiValued ? preselect : preselect[0];
        }
    }
}
